import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Modal, ContextMenu } from '../components/ui';
import { UserAvatar } from '../components/UserAvatar';
import { useAuth } from '../contexts/AuthContext';
import { ALL_ROLES, ROLE_METADATA, Role, roleLabel } from '../types/auth';
import {
  ManagedUser,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  setUserActive,
  resetUserPassword,
} from '../lib/usersApi';
import './UserManagement.css';
import { Trans, useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface UserManagementProps {
  apiUrl: string;
}

const ROLE_BADGE_CLASS: Record<Role, string> = {
  Admin: 'um-role-admin',
  SecurityOfficer: 'um-role-secofficer',
  Analyst: 'um-role-analyst',
  AgentOperator: 'um-role-operator',
  Viewer: 'um-role-viewer',
};

const emptyForm = { userId: '', displayName: '', email: '', role: 'Analyst' as Role, password: '' };

const UserManagement: React.FC<UserManagementProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { userAttributes } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Modals
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editUser, setEditUser] = useState<ManagedUser | null>(null);
  const [resetUser, setResetUser] = useState<ManagedUser | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [deleteUserTarget, setDeleteUserTarget] = useState<ManagedUser | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listUsers(apiUrl));
    } catch (e: any) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 3500);
  };

  const handleCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      await createUser(apiUrl, {
        userId: form.userId.trim(),
        password: form.password,
        role: form.role,
        displayName: form.displayName.trim() || undefined,
        email: form.email.trim() || undefined,
        mustChangePassword: true,
      });
      setShowAdd(false);
      setForm(emptyForm);
      flash(`User “${form.userId}” created.`);
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setBusy(true);
    setError(null);
    try {
      await updateUser(apiUrl, editUser.userId, {
        role: editUser.role,
        displayName: editUser.displayName || undefined,
        email: editUser.email || undefined,
      });
      setEditUser(null);
      flash('User updated.');
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to update user');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!resetUser) return;
    setBusy(true);
    setError(null);
    try {
      await resetUserPassword(apiUrl, resetUser.userId, resetPw);
      setResetUser(null);
      setResetPw('');
      flash(`Password reset for “${resetUser.userId}”. They must change it at next login.`);
    } catch (e: any) {
      setError(e.message || 'Failed to reset password');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (u: ManagedUser) => {
    setError(null);
    try {
      await setUserActive(apiUrl, u.userId, !u.active);
      flash(`User “${u.userId}” ${u.active ? 'disabled' : 'enabled'}.`);
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to update account status');
    }
  };

  const handleDelete = async () => {
    if (!deleteUserTarget) return;
    setBusy(true);
    setError(null);
    try {
      await deleteUser(apiUrl, deleteUserTarget.userId);
      setDeleteUserTarget(null);
      flash('User deleted.');
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to delete user');
    } finally {
      setBusy(false);
    }
  };

  const isSelf = (u: ManagedUser) => u.userId === userAttributes?.userId;

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="users" size={20} /> {t('pages.userManagement.userManagement')}
          </h1>
          <p className="subtitle">{t('pages.userManagement.createAndManageAgenti5UsersRoles')}</p>
        </div>
        <button className="ent-btn primary" onClick={() => { setForm(emptyForm); setError(null); setShowAdd(true); }}>
          <Icon name="plus" /> {t('pages.userManagement.addUser')}
        </button>
      </div>

      {notice && (
        <div className="um-notice-success">
          <Icon name="checkCircle" size={15} /> {notice}
        </div>
      )}
      {error && !showAdd && !editUser && !resetUser && !deleteUserTarget && (
        <div className="um-notice-error">
          <Icon name="warning" size={15} /> {error}
        </div>
      )}

      <div className="ent-table-wrap scroll">
        <table className="ent-table">
          <thead>
            <tr>
              <th>{t('pages.userManagement.user')}</th>
              <th style={{ width: 150 }}>{t('pages.userManagement.role')}</th>
              <th style={{ width: 100 }}>{t('pages.userManagement.status')}</th>
              <th style={{ width: 150 }}>{t('pages.userManagement.lastLogin')}</th>
              <th style={{ width: 60, textAlign: 'right' }}>{t('pages.userManagement.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="um-empty-cell">{t('pages.userManagement.loadingUsers')}</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="um-empty-cell">{t('pages.userManagement.noUsersYetClickAddUser')}</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.userId}>
                  <td>
                    <div className="um-user-cell">
                      <UserAvatar displayName={u.displayName || u.userId} email={u.email || undefined} role={u.role} size="sm" />
                      <div className="um-user-info">
                        <span className="um-user-name">
                          {u.displayName || u.userId}
                          {isSelf(u) && <span className="um-you-tag">{t('pages.userManagement.you')}</span>}
                        </span>
                        <span className="um-user-id">{u.userId}{u.email ? ` · ${u.email}` : ''}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`um-role-badge ${ROLE_BADGE_CLASS[u.role]}`}>{roleLabel(u.role)}</span>
                  </td>
                  <td>
                    {u.active ? (
                      <span className="um-status um-status-active"><span className="um-dot" /> {t('pages.userManagement.active')}</span>
                    ) : (
                      <span className="um-status um-status-disabled"><span className="um-dot" /> {t('pages.userManagement.disabled')}</span>
                    )}
                    {u.mustChangePassword && (
                      <span className="um-pw-flag" title={t('pages.userManagement.mustChangePasswordAtNextLogin')}>
                        <Icon name="key" size={11} /> {t('pages.userManagement.reset')}
                      </span>
                    )}
                  </td>
                  <td className="um-muted">{u.lastLogin ? parseServerDate(u.lastLogin).toLocaleString() : t('pages.userManagement.never')}</td>
                  <td style={{ textAlign: 'right' }}>
                    <ContextMenu
                      items={[
                        { label: t('pages.userManagement.editUser'), icon: 'info', onClick: () => { setEditUser({ ...u }); setError(null); } },
                        { label: t('pages.userManagement.resetPassword'), icon: 'key', onClick: () => { setResetUser(u); setResetPw(''); setError(null); } },
                        {
                          label: u.active ? 'Disable account' : 'Enable account',
                          icon: u.active ? 'lock' : 'checkCircle',
                          onClick: () => handleToggleActive(u),
                        },
                        { label: t('pages.userManagement.deleteUser'), icon: 'trash', danger: true, onClick: () => { setDeleteUserTarget(u); setError(null); } },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add User modal */}
      {showAdd && (
        <Modal
          title={t('pages.userManagement.addUser')}
          icon="users"
          onClose={() => !busy && setShowAdd(false)}
          maxWidth={480}
          footer={
            <>
              <button className="ent-btn ghost" onClick={() => setShowAdd(false)} disabled={busy}>{t('pages.userManagement.cancel')}</button>
              <button
                className="ent-btn primary"
                onClick={handleCreate}
                disabled={busy || !form.userId.trim() || !form.password}
              >
                <Icon name="plus" /> {busy ? t('pages.userManagement.creating') : t('pages.userManagement.createUser')}
              </button>
            </>
          }
        >
          <div className="ent-field">
            <label>{t('pages.userManagement.username')} <span className="um-req">*</span></label>
            <input className="ent-input" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} placeholder={t('pages.userManagement.eGJsmith')} autoComplete="off" />
          </div>
          <div className="um-form-row">
            <div className="ent-field">
              <label>{t('pages.userManagement.displayName')}</label>
              <input className="ent-input" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder={t('pages.userManagement.janeSmith')} />
            </div>
            <div className="ent-field">
              <label>{t('pages.userManagement.email')}</label>
              <input className="ent-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('pages.userManagement.janeCompanyCom')} />
            </div>
          </div>
          <div className="ent-field">
            <label>{t('pages.userManagement.role')} <span className="um-req">*</span></label>
            <select className="ent-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>{roleLabel(r)}</option>
              ))}
            </select>
            <p className="um-role-hint">{ROLE_METADATA.find((r) => r.role === form.role)?.description}</p>
          </div>
          <div className="ent-field">
            <label>{t('pages.userManagement.temporaryPassword')} <span className="um-req">*</span></label>
            <input className="ent-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t('pages.userManagement.min8CharsUpperLowerNumber')} autoComplete="off" />
            <p className="um-role-hint">{t('pages.userManagement.theUserWillBeRequiredTo')}</p>
          </div>
          {error && <div className="um-notice-error" style={{ marginBottom: 0 }}><Icon name="warning" size={15} /> {error}</div>}
        </Modal>
      )}

      {/* Edit User modal */}
      {editUser && (
        <Modal
          title={t('pages.userManagement.editUser', { userId: editUser.userId })}
          icon="info"
          onClose={() => !busy && setEditUser(null)}
          maxWidth={460}
          footer={
            <>
              <button className="ent-btn ghost" onClick={() => setEditUser(null)} disabled={busy}>{t('pages.userManagement.cancel')}</button>
              <button className="ent-btn primary" onClick={handleEdit} disabled={busy}><Icon name="check" /> {busy ? t('pages.userManagement.saving') : t('pages.userManagement.saveChanges')}</button>
            </>
          }
        >
          <div className="ent-field">
            <label>{t('pages.userManagement.displayName')}</label>
            <input className="ent-input" value={editUser.displayName || ''} onChange={(e) => setEditUser({ ...editUser, displayName: e.target.value })} />
          </div>
          <div className="ent-field">
            <label>{t('pages.userManagement.email')}</label>
            <input className="ent-input" type="email" value={editUser.email || ''} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} />
          </div>
          <div className="ent-field">
            <label>{t('pages.userManagement.role')}</label>
            <select className="ent-input" value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value as Role })}>
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>{roleLabel(r)}</option>
              ))}
            </select>
            <p className="um-role-hint">{ROLE_METADATA.find((r) => r.role === editUser.role)?.description}</p>
          </div>
          {error && <div className="um-notice-error" style={{ marginBottom: 0 }}><Icon name="warning" size={15} /> {error}</div>}
        </Modal>
      )}

      {/* Reset password modal */}
      {resetUser && (
        <Modal
          title={t('pages.userManagement.resetPasswordTitle', { userId: resetUser.userId })}
          icon="key"
          onClose={() => !busy && setResetUser(null)}
          maxWidth={440}
          footer={
            <>
              <button className="ent-btn ghost" onClick={() => setResetUser(null)} disabled={busy}>{t('pages.userManagement.cancel')}</button>
              <button className="ent-btn primary" onClick={handleReset} disabled={busy || !resetPw}><Icon name="key" /> {busy ? t('pages.userManagement.resetting') : t('pages.userManagement.resetPassword')}</button>
            </>
          }
        >
          <p className="um-modal-text">{t('pages.userManagement.setATemporaryPassword')} <strong>{resetUser.displayName || resetUser.userId}</strong> {t('pages.userManagement.willBeRequiredToChangeIt')}</p>
          <div className="ent-field">
            <label>{t('pages.userManagement.temporaryPassword')}</label>
            <input className="ent-input" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder={t('pages.userManagement.min8CharsUpperLowerNumber')} autoComplete="off" />
          </div>
          {error && <div className="um-notice-error" style={{ marginBottom: 0 }}><Icon name="warning" size={15} /> {error}</div>}
        </Modal>
      )}

      {/* Delete confirmation */}
      {deleteUserTarget && (
        <Modal
          title={t('pages.userManagement.deleteUser')}
          icon="trash"
          onClose={() => !busy && setDeleteUserTarget(null)}
          maxWidth={420}
          footer={
            <>
              <button className="ent-btn ghost" onClick={() => setDeleteUserTarget(null)} disabled={busy}>{t('pages.userManagement.cancel')}</button>
              <button className="ent-btn danger" onClick={handleDelete} disabled={busy}><Icon name="trash" /> {busy ? t('pages.userManagement.deleting') : t('pages.userManagement.delete')}</button>
            </>
          }
        >
          <p className="um-modal-text">
            <Trans
              i18nKey="pages.userManagement.permanentlyDeleteConfirm"
              values={{ name: deleteUserTarget.displayName || deleteUserTarget.userId, userId: deleteUserTarget.userId }}
              components={{ s: <strong /> }}
            />
          </p>
          {error && <div className="um-notice-error" style={{ marginBottom: 0 }}><Icon name="warning" size={15} /> {error}</div>}
        </Modal>
      )}
    </div>
  );
};

export default UserManagement;
